import BrandImg from 'assets/images/details/BrandWorkstream.svg'
import PeopleImg from 'assets/images/details/People.svg'
import ProductImg from 'assets/images/details/ProductWorkstream.svg'
import beliefs from 'assets/images/workingModeSelection/beliefs.svg'
import brandEdge from 'assets/images/workingModeSelection/BrandEdge.svg'
import buildingRelevance from 'assets/images/workingModeSelection/buildingRelevance.svg'
import bvaTemplate from 'assets/images/workingModeSelection/BvaTemplate.svg'
import capturing from 'assets/images/workingModeSelection/capturing.svg'
import capturingBrand from 'assets/images/workingModeSelection/CapturingBrand.svg'
import Category from 'assets/images/workingModeSelection/category.svg'
import miniManefesto from 'assets/images/workingModeSelection/MiniManefesto.svg'
import ourVowtoOurConsumer from 'assets/images/workingModeSelection/ourVowToOurConsumers.svg'
import productEdge from 'assets/images/workingModeSelection/productedge.svg'
import shortList from 'assets/images/workingModeSelection/shortlist.svg'
import shortlistBrand from 'assets/images/workingModeSelection/ShortlistBrand.svg'
import shortlistWork from 'assets/images/workingModeSelection/shortlistwork.svg'
import surfacingTensions from 'assets/images/workingModeSelection/surfacingTensions.svg'
import usVsCategory from 'assets/images/workingModeSelection/usvscategory.svg'
import usVsCategoryBrand from 'assets/images/workingModeSelection/usVSCategoryBrand.svg'

export interface DetailSubsection {
  label: string
  description?: string
  dataSources?: string
  note?: string
  showDownloadButton?: boolean
  downloadUrl?: string
  bullets?: string[]
}

export interface TeamInfo {
  performsRecommendsLabel: string
  performsRecommends: string
  coreTeam: string
  extendedTeam?: string
}

export interface WorkstreamDetailModalData {
  title: string
  color: string
  column1Heading: string
  subsections: DetailSubsection[]
  team?: TeamInfo
  teamParagraph?: string
  teamNote?: string
  outputText: string
  outputTextBold?: string
  outputTextBoldPhrases?: string[]
  steps: string[]
  bgColor?: string
  imgUrl?: string
  inputBgColor?: string
  stepsBgColor?: string
  teamBgColor?: string
  sessionToolsBgColor?: string
}

export interface DetailBand {
  heading: string
  color: string
  subsections: DetailSubsection[]
}

export interface DetailThumbnail {
  label: string
  imgUrl?: string
}

export interface BandedWorkstreamDetailModalData {
  title: string
  bands: DetailBand[]
  outputThumbnails?: DetailThumbnail[]
  outputbgColor?: string
  muralUrl?: string
  steps?: string[]
  layout: 'right-rail' | 'split-band'
}

const SPRINT_WORKSTREAM_INPUT: DetailSubsection[] = [
  { label: 'Stimuli From 3 Workstreams', description: 'Presentations and immersive crash course on key findings.' },
  {
    label: 'Inspiring Bites From The Outside World (Optional)',
    description:
      'e.g., Keynote speaker from outside the organization but relevant to the territory of exploration for the brand.',
  },
  {
    label: 'Meticulous Logistics',
    description: 'Inspiring space close to the brand universe and minute-by-minute time boxed activities.',
  },
]

const SPRINT_WORKSTREAM_STEPS: string[] = [
  'Now that we have all the content necessary, we are ready to get into our sprint to converge and finalize the BVA. The objective is to review each of the 3 parts and define the draft Meaningful Brand Role.',
  'A facilitator should coordinate with captains of workstreams to set up agenda flow, stimuli, exercises and logistics.',
  'On Day 1, all workstreams present their stimuli and/or proposals. The rest of the day, 3 cross-functional teams work in parallel to complete the templates (see below) before sharing among everyone and defining the Meaningful Brand Role.',
  'On Day 2, the sponsor and lead OU reps provide feedback and the team iterates accordingly. The rest of the day is focused on discussing implications (see next slide) and next steps.',
]

const workstreamDetailModals: Record<string, WorkstreamDetailModalData> = {
  'async-people-detail': {
    title: 'People Workstream',
    color: '#FFA89D',
    column1Heading: 'What you need',
    subsections: [
      {
        label: 'Growth Audience',
        description: 'Intender and Neutral profiles that translate business growth into active recruitment.',
        dataSources: 'BEACH, need-states, demand spaces, psychographics, Audience Origin.',
        note: 'Refer to the Audience Architecture Framework for guidance.',
        showDownloadButton: true,
      },
      {
        label: 'Design Audience Definition & Existing HI Research',
        description:
          'Define a relatable social identity that translates the growth audience into an aspiration and conduct deep holistic understanding.',
        dataSources:
          'Psychographics, frontal research, ad hoc qualitative, e.g. consumer immersions, Mind Maps, social listening.',
        note: 'Refer back to the Immersion Playbook for guidance.',
        showDownloadButton: true,
      },
    ],
    team: {
      performsRecommendsLabel: 'Performs / Recommends',
      performsRecommends: 'Global Brand Lead',
      coreTeam: 'XP/ Creative, OU CMOs/OU brand team representative from core EP Combos',
      extendedTeam: 'Global Insight repr, External research agencies, WPP Strategist',
    },
    outputText: "Shortlist for the audience's beliefs/values, needs, and tension (based on relevant templates)",
    outputTextBold: "audience's beliefs/values, needs, and tension",
    steps: [
      'Assessment existing knowledge.',
      'Commission fieldwork among team.',
      'Complete the empathy prework.',
      'Virtual session to present most interesting findings among core team.',
      '2x virtual cross-pollinations with other workstreams: share key findings to ensure templates are filled with as much alignment as possible.',
      'Fill out the People templates as a core team, follow the Mural board for detailed format instructions.',
      'Prep the sprint People stimuli for the other workstreams.',
    ],
    bgColor: '#FFE8E4',
    imgUrl: PeopleImg,
  },
  'async-product-detail': {
    title: 'Product Workstream',
    color: '#A7E7ED',
    column1Heading: 'Input',
    subsections: [
      {
        label: "People's Needs + Expectations",
        description: 'What is the reason for drinking and desired product experience?',
        dataSources: 'Need-state deep dive, frontend research, signature experience (eg Mind Maps).',
      },
      {
        label: 'Role Of The Category',
        description: 'What the category brings to people today + tomorrow: benefits & sensorial cues.',
        dataSources: 'U&A, product experience testing, R&D frontend research, future trends in innovation.',
      },
      {
        label: 'Uniqueness + Superiority',
        description: 'How we stand out amidst the category + competition today and in the future.',
        dataSources: 'MoT Superiority Tracker, competitive review, evolving portfolios.',
      },
    ],
    team: {
      performsRecommendsLabel: 'Performs / Recommends',
      performsRecommends: 'Global Brand Lead',
      coreTeam: 'Global Categ rep, Global Insights rep, GDI, TCR',
      extendedTeam: 'SRA, OU CMOs/OU brand team representative from core EP Combos',
    },
    outputText:
      'Shortlist for superiority insights, RTBs, functional benefits, sensorial cues, and Product Edge (based on relevant templates)',
    outputTextBold: 'superiority insights, RTBs, functional benefits, sensorial cues, and Product Edge',
    steps: [
      'Commission fieldwork for missing critical input after kickoff.',
      'Audit existing knowledge (incl.LH/R&D review of past docs to align on key messages and codify inputs).',
      'Review Signature Experience, suggest interactive session to discuss key learnings. If this is the first time the team sees it then needs to be a longer discussion; if not, a fast regrouping to define critical elements.',
      'Virtual session to present most interesting findings among core team.',
      'Virtual cross-pollination with other workstreams: share key findings to ensure templates are filled with as much alignment as possible.',
      'Virtual session to fill out the Product templates as a core team. Follow the Mural board for detailed format instructions.',
      'Prep the sprint Product stimuli for the other workstreams.',
    ],
    bgColor: '#E8F7F5',
    imgUrl: ProductImg,
  },
  'async-brand-detail': {
    title: 'Brand Workstream',
    color: '#91DDA4',
    column1Heading: 'What you need',
    subsections: [
      {
        label: 'Brand Assets',
        description: 'Identification of visual and design cues, as well as activation assets.',
        dataSources:
          'VIS, brand comms, design archives, semiotics, competitive review, distinctive assets testing; leveraging the Human disciplines.',
      },
      {
        label: 'Brand Power',
        description: "Identification of the brand's strength and its connection to people.",
        dataSources: 'BEACH equity, Brand Power, psychographic & brand reports.',
      },
      {
        label: 'Hero Occasions',
        description: 'Definition of staging occasions to build equity and dramatize need.',
        dataSources: 'U&A, asset review, business & system reviews.',
      },
    ],
    team: {
      performsRecommendsLabel: 'Owner / Recommends',
      performsRecommends: 'Global Brand Lead',
      coreTeam: 'Global XP representative, WPP Creative Strategy, Insights rep, Design & Packaging rep',
      extendedTeam: 'OU CMOs/OU brand team representative from core EP Combos, RGM',
    },
    outputText:
      'Shortlist for superiority insights, RTBs, functional benefits, sensorial cues, and Product Edge (based on relevant templates)',
    outputTextBold: 'superiority insights, RTBs, functional benefits, sensorial cues, and Product Edge',
    steps: [
      'Brand Assessment based on available data.',
      'Commission fieldwork for missing critical input.',
      'Deep dive in Brand Archives (communication, owned assets, positioning, history).',
      '1:1s as needed to discuss Brand Philosophy and pricing position.',
      'Virtual session to present most interesting findings among core team.',
      'Virtual cross-pollination with other workstreams: share key findings to ensure templates are filled out with as much alignment as possible.',
      'Virtual session to fill out the Brand templates as a core team. Follow the Mural board for detailed format instructions.',
      'Prep the sprint Brand stimuli for the other workstreams.',
    ],
    bgColor: '#EDFAF0',
    imgUrl: BrandImg,
  },
  'async-sprint-detail': {
    title: 'Sprint Workstream',
    color: 'gradient',
    column1Heading: 'Input',
    subsections: SPRINT_WORKSTREAM_INPUT,
    teamParagraph:
      'All SMEs that are part of the core teams for each of the workstreams (carrying knowledge on People, Product, Brand) participate physically (max. 15 people)',
    teamNote: '+ Sponsor — For debrief and feedback check-in on Day 2',
    outputText: 'Review each part (People, Product, Brand) and draft Meaningful Brand Role together',
    outputTextBoldPhrases: ['Review each part', 'draft Meaningful Brand Role together'],
    steps: SPRINT_WORKSTREAM_STEPS,
    bgColor: '#EAF5EF',
    inputBgColor:
      'linear-gradient(97deg, rgba(254, 86, 14, 0.50) 7.96%, rgba(169, 153, 126, 0.50) 30.13%, rgba(107, 200, 205, 0.50) 56.23%, rgba(107, 202, 161, 0.50) 80.43%, rgba(107, 203, 131, 0.50) 96.58%), #FFF',
    stepsBgColor:
      'linear-gradient(138deg, rgba(254, 86, 14, 0.10) 7.16%, rgba(169, 153, 126, 0.10) 27.1%, rgba(107, 200, 205, 0.10) 50.57%, rgba(107, 202, 161, 0.10) 72.34%, rgba(107, 203, 131, 0.10) 86.86%), #FFF',
    teamBgColor:
      'linear-gradient(93deg, rgba(254, 86, 14, 0.10) 9.48%, rgba(169, 153, 126, 0.10) 50.65%, rgba(107, 200, 205, 0.10) 99.11%, rgba(107, 202, 161, 0.10) 144.04%, rgba(107, 203, 131, 0.10) 174.04%), #FFF',
    sessionToolsBgColor:
      'linear-gradient(98deg, rgba(254, 86, 14, 0.10) -108.29%, rgba(169, 153, 126, 0.10) -60.26%, rgba(107, 200, 205, 0.10) -3.72%, rgba(107, 202, 161, 0.10) 48.71%, rgba(107, 203, 131, 0.10) 83.71%), #FFF',
  },
}

const bandedWorkstreamDetailModals: Record<string, BandedWorkstreamDetailModalData> = {
  'e2e-day1-detail': {
    title: 'People Workstream',
    layout: 'right-rail',
    bands: [
      {
        heading: 'Pre-work Components',
        color: '#FFA89D',
        subsections: [
          {
            label: 'Growth Audience',
            description: 'Intender and Neutral profiles that translates business growth into active recruitment.',
            dataSources: 'BEACH, Need-states, demand spaces, psychographics, and Audience Origin.',
            note: 'Refer to the Audience Architecture Framework for guidance.',
            showDownloadButton: true,
          },
          {
            label: 'Design Audience Definition & Existing HI Research',
            description:
              'Define a relatable social identity that translates the growth audience in an aspiration and conduct deep holistic understanding.',
            dataSources:
              'Psychographics, frontend research, ad hoc qualitative research, e.g., consumer immersions, Mind Maps, social listening.',
            note: 'Refer back to the Immersion Playbook for guidance.',
            showDownloadButton: true,
            downloadUrl:
              'https://onedrive.coca-cola.com/:f:/g/personal/skuper_coca-cola_com1/Er8PYxYAw2dKunujRPjb9kMBcV2YXOpZFmOVR8BSKoz8IQ?e=CJ3Pdo',
          },
        ],
      },
      {
        heading: 'Input',
        color: '#ffe8e4',
        subsections: [
          {
            label: 'Growth Audience',
            bullets: ['Beach', 'Need-state deep dives', 'Demand spaces', 'Audience Origin', 'PRISM', 'Consumer flows'],
          },
          { label: 'Design Audience', bullets: ['Psychographic lens', 'Frontend research'] },
          {
            label: 'Design Audience Empathy',
            bullets: ['Ad hoc qualitative e.g. consumer immersions, Mind Maps, social listening'],
          },
        ],
      },
    ],
    outputbgColor: '#FFE8E4',
    muralUrl:
      'https://app.mural.co/t/reborrn0190/m/reborrn0190/1741803875130/917b26cb6886cf897e1b7145046bcdb99e8249d8?sender=u63412ace9107fd7363ca8493',
    outputThumbnails: [
      { label: '5 Beliefs On Life', imgUrl: beliefs },
      { label: 'How They See The Category', imgUrl: Category },
      { label: 'Surfacing Tensions', imgUrl: surfacingTensions },
      { label: 'Shortlist', imgUrl: shortList },
    ],
  },
  'e2e-day2-detail': {
    title: 'Product Workstream',
    layout: 'right-rail',
    bands: [
      {
        heading: 'Pre-work Components',
        color: '#A7E7ED',
        subsections: [
          {
            label: "People's Needs + Expectations",
            description: 'Know why people are there: desired experience and reasons for drinking.',
            dataSources: 'Need-state deep dive, frontend research, signature experience (eg Mind Maps).',
          },
          {
            label: 'Role Of The Category',
            description: 'What category brings to people today + tomorrow: benefits + sensorial cues.',
            dataSources: 'U&A, product experience testing, R&D frontend research, future trends in innovation.',
          },
          {
            label: 'Our Uniqueness + Superiority',
            description: 'How we stand out amidst the category + competition today and in the future.',
            dataSources: 'MoT Superiority Tracker, Product strength, competitive review, evolving portfolios.',
          },
        ],
      },
      {
        heading: 'Input',
        color: '#e8f7f5',
        subsections: [
          {
            label: "People's Needs + Expectations",
            bullets: ['Need-state deep dive', 'Frontend research', 'Signature experience (eg Mind Maps)'],
          },
          {
            label: 'Role Of The Category',
            bullets: [
              'U&A',
              'Product experience testing',
              'R&D frontend research',
              'R&D sensory mapping & category knowledge',
              'Future trends in innovation',
            ],
          },
          {
            label: 'Our Uniqueness + Superiority',
            bullets: ['Taste Superiority Tracker', 'Product strength', 'Competitive review', 'Evolving portfolios'],
          },
        ],
      },
    ],
    outputbgColor: '#E8F7F5',
    muralUrl:
      'https://app.mural.co/t/reborrn0190/m/reborrn0190/1741803875130/917b26cb6886cf897e1b7145046bcdb99e8249d8?sender=u63412ace9107fd7363ca8493',

    outputThumbnails: [
      { label: 'Capturing Ahas', imgUrl: capturing },
      { label: 'Us vs Category', imgUrl: usVsCategory },
      { label: 'Shortlist', imgUrl: shortlistWork },
      { label: 'Product Edge', imgUrl: productEdge },
    ],
  },
  'e2e-day3-detail': {
    title: 'Brand Workstream',
    layout: 'right-rail',
    bands: [
      {
        heading: 'Pre-work Components',
        color: '#91DDA4',
        subsections: [
          {
            label: 'Brand Assets & Heritage',
            description: 'Identification of visual and design cues, as well as activation assets.',
            dataSources: 'VIS, brand comms, design archives, semiotics, competitive review, creative strategy.',
          },
          {
            label: 'Brand Power',
            description: "Identification of the brand's strength and its connection to people.",
            dataSources: 'BEACH Equity, Brand Power, psychographic & brand reports.',
          },
          {
            label: 'Hero Occasions',
            description: 'Definition of staging occasions to build equity and dramatize need.',
            dataSources: 'U&A, asset review, business & system reviews.',
          },
        ],
      },
      {
        heading: 'Input',
        color: '#edfaf0',
        subsections: [
          {
            label: 'Brand Assets',
            bullets: [
              'VIS',
              'Brand comms',
              'Design archives',
              'Semiotics',
              'Competitive review (incl. MoT comms)',
              'Creative strategy',
              'Distinctive asset testing',
              'Signature experience',
            ],
          },
          { label: 'Brand Power', bullets: ['BEACH Equity', 'Brand Power', 'Psychographic & brand reports'] },
          { label: 'Hero Occasions', bullets: ['U&A', 'Asset review', 'Business & system reviews'] },
        ],
      },
    ],
    outputbgColor: '#EDFAF0',
    muralUrl:
      'https://app.mural.co/t/reborrn0190/m/reborrn0190/1741803875130/917b26cb6886cf897e1b7145046bcdb99e8249d8?sender=u63412ace9107fd7363ca8493',
    outputThumbnails: [
      { label: 'Capturing Ahas', imgUrl: capturingBrand },
      { label: 'Us vs Category', imgUrl: usVsCategoryBrand },
      { label: 'Shortlist', imgUrl: shortlistBrand },
      { label: 'Brand Edge', imgUrl: brandEdge },
    ],
  },
  'e2e-day4-detail': {
    title: 'Sprint Workstream',
    layout: 'split-band',
    bands: [
      {
        heading: 'Pre-work Components',
        color:
          'linear-gradient(129deg, rgba(254, 86, 14, 0.50) 7.96%, rgba(169, 153, 126, 0.50) 30.13%, rgba(107, 200, 205, 0.50) 56.23%, rgba(107, 202, 161, 0.50) 80.43%, rgba(107, 203, 131, 0.50) 96.58%), #FFF',
        subsections: SPRINT_WORKSTREAM_INPUT,
      },
      {
        heading: 'Input',
        color:
          'linear-gradient(91deg, rgba(254, 86, 14, 0.20) 22.15%, rgba(169, 153, 126, 0.20) 83.82%, rgba(107, 200, 205, 0.20) 156.41%, rgba(107, 202, 161, 0.20) 223.73%, rgba(107, 203, 131, 0.20) 268.66%), #FFF',
        subsections: [
          { label: 'Templates From Days 1-3', bullets: ['People templates', 'Product templates', 'Brand templates'] },
        ],
      },
    ],
    outputbgColor:
      'linear-gradient(94deg, rgba(254, 86, 14, 0.20) -35.78%, rgba(169, 153, 126, 0.20) -6.62%, rgba(107, 200, 205, 0.20) 27.71%, rgba(107, 202, 161, 0.20) 59.54%, rgba(107, 203, 131, 0.20) 80.79%), #FFF',
    outputThumbnails: [
      { label: 'Building Relevance', imgUrl: buildingRelevance },
      { label: 'Our Vow To Our Consumers', imgUrl: ourVowtoOurConsumer },
      { label: 'Mini Manifest', imgUrl: miniManefesto },
      { label: 'BVA Template', imgUrl: bvaTemplate },
    ],
  },
}

export const getWorkstreamDetailModalData = (id: string): WorkstreamDetailModalData | undefined =>
  workstreamDetailModals[id]
export const getBandedWorkstreamDetailModalData = (id: string): BandedWorkstreamDetailModalData | undefined =>
  bandedWorkstreamDetailModals[id]
